import { type Channel, type LineItem } from '@commercetools/platform-sdk'
import { Link, useLoaderData, useLocation, useNavigate } from '@remix-run/react'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import Callout from '#app/components/common/callout'
import PlentyBody from '#app/components/typography/plenty-body'
import PlentyHeading from '#app/components/typography/plenty-heading'
import { Button } from '#app/components/ui/button'
import { Drawer, DrawerContent } from '#app/components/ui/drawer'
import { Icon } from '#app/components/ui/icon'
import useAmplitudeContext from '#app/hooks/use-amplitude-context'
import { usePlentyMediaQuery } from '#app/hooks/use-plenty-media-query'
import { useFetcherWithReset } from '#app/hooks/useFetcherWithReset'
import {
	type action,
	type loader,
} from '#app/routes/_public+/product+/$styleNumber'
import { useCartStore } from '#app/store/cart'
import { cn, deepClone } from '#app/utils/misc'

import { type PlentyLineItem } from '#types/cart'
import BagItemContent from './bag-item-content-mobile'
import ProductBagCard from './product-bag-card'

export interface BagItemProps {
	status: string
	newItems: LineItem[] | undefined
	newDistributionChannel: Channel | undefined
}
const BagItem = () => {
	const portalContainer = useRef<HTMLElement | null>(null)
	const { cart } = useCartStore()
	const topNavbar = useRef<HTMLElement | null>(null)
	const [open, setOpen] = useState(false)
	const [productItemData, setProductItemData] = useState<BagItemProps | null>(
		null,
	)
	const { storesChannels } = useLoaderData<typeof loader>()

	const [nudgeMessage, setNudgeMessage] = useState<string | null>(null)
	const navigate = useNavigate()
	const location = useLocation()
	const searchParams = new URLSearchParams(location.search)

	const isDesktop = usePlentyMediaQuery('(min-width: 820px)')
	const productFetcher = useFetcherWithReset<typeof action>({ key: 'addToBag' })
	let { t } = useTranslation('shopping_bag')
	const { trackAmplitudeEvent } = useAmplitudeContext()
	useEffect(() => {
		if (document) {
			portalContainer.current = document.getElementById('bag-item-portal')
			topNavbar.current = document.getElementById('top')
		}

		return () => {
			productFetcher.reset()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (
			productFetcher.data &&
			productFetcher.data.newItems &&
			productFetcher.data.newItems?.length > 0
		) {
			const copiedData = deepClone(productFetcher.data) as BagItemProps
			setProductItemData(copiedData)
			productFetcher.reset()
			setOpen(true)
			if (copiedData.newDistributionChannel) {
				setNudgeMessage(t('product_details_page:nudge_message'))
				const storeChannelKey =
					storesChannels.find(
						sc => sc.key === copiedData?.newDistributionChannel?.key,
					)?.storeKey || ''

				searchParams.append('store', storeChannelKey)
				trackAmplitudeEvent('store selected', {
					storeChannelId: copiedData.newDistributionChannel.id,
					storeKey: storeChannelKey,
					store: copiedData.newDistributionChannel?.name?.en,
				})
				navigate(`${location.pathname}?${searchParams.toString()}`, {
					replace: true,
					preventScrollReset: true,
				})
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [productFetcher.data])

	useEffect(() => {
		let timeoutId: NodeJS.Timeout
		if (open) {
			timeoutId = setTimeout(() => {
				productFetcher.reset()
			}, 500)
		}

		return () => {
			clearTimeout(timeoutId)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open])

	if (!portalContainer.current) {
		return null
	}
	if (!topNavbar.current) {
		return null
	}
	const storeSelectorHeight = isDesktop ? 38 : 0

	const top =
		topNavbar.current.getBoundingClientRect().top +
		topNavbar.current.clientHeight +
		storeSelectorHeight
	if (isDesktop) {
		return createPortal(
			<AnimatePresence>
				{open ? (
					<motion.div
						key={'bag-item-portal'}
						layout
						className={cn(
							'fixed left-0 z-50 h-screen w-full bg-black/50 lg:block',
						)}
						style={{
							top: topNavbar.current && isDesktop ? `${top}px` : `0`,
						}}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						onClick={() => setOpen(false)}
					>
						<motion.div
							key={'bag-item-card'}
							initial={{ opacity: 0, y: isDesktop ? '-100%' : '100%' }}
							animate={{ opacity: 1, y: '0' }}
							exit={{ opacity: 0, x: isDesktop ? '100%' : '-100%' }}
							className={cn(
								'absolute z-50 flex w-full flex-col gap-y-3 border-0 bg-white px-4 pt-4 pb-2 lg:top-0 lg:right-12 lg:w-fit lg:min-w-[400px] lg:rounded-b-lg lg:p-4',
								!isDesktop ? 'bottom-0' : 'lg:top-0',
							)}
							style={{
								bottom: topNavbar.current && !isDesktop ? `0` : '',
							}}
							onClick={e => e.stopPropagation()}
						>
							<div className="flex flex-row justify-between">
								<div className="flex flex-row items-start gap-x-2">
									<Icon name="check" size="lg" className="text-mint-green-60" />
									<PlentyHeading as="h7">
										{t('added_to_your_shopping_bag')}
									</PlentyHeading>
								</div>
								<Icon
									name="close"
									size="lg"
									onClick={() => setOpen(false)}
									className="cursor-pointer"
								/>
							</div>
							{nudgeMessage && (
								<div>
									<PlentyBody>
										{t('product_details_page:nudge_message_store_info', {
											store:
												productItemData?.newDistributionChannel?.name?.en ||
												'store',
										})}
									</PlentyBody>
								</div>
							)}
							<div>
								{productItemData?.newItems &&
									productItemData?.newItems.map(item => {
										if (!item) return null
										return (
											<ProductBagCard
												key={item.id}
												plentyLineItem={item as PlentyLineItem}
											/>
										)
									})}
							</div>
							<div className="flex flex-col gap-2 lg:flex-row lg:gap-x-2">
								<Link to={'/shopping-bag'}>
									<Button
										variant="secondary"
										size="secondary"
										fullWidth
										data-testid="pdp-go-to-shopping-bag-button"
									>
										{t('go_to_shopping_bag')}{' '}
										{`(${getTotalItemsFromCart(cart?.lineItems || [])})`}
									</Button>
								</Link>
								<Link to={'/checkout'} className="flex grow">
									<Button
										fullWidth
										size="secondary"
										data-testid="pdp-go-to-checkout-button"
									>
										{t('checkout:checkout_header')}
									</Button>
								</Link>
							</div>
							{nudgeMessage && (
								<div>
									<Callout color="secondary" className="max-w-[400px]">
										<PlentyBody size="md">{nudgeMessage}</PlentyBody>
									</Callout>
								</div>
							)}
						</motion.div>
					</motion.div>
				) : null}
			</AnimatePresence>,
			portalContainer.current,
		)
	} else {
		return (
			<Drawer open={open} onOpenChange={setOpen}>
				<DrawerContent>
					<BagItemContent
						productItemData={productItemData}
						nudgeMessage={nudgeMessage}
					/>
				</DrawerContent>
			</Drawer>
		)
	}
}

export function getTotalItemsFromCart(cart: LineItem[]) {
	if (!cart) return 0
	return cart.reduce((acc, item) => acc + item.quantity, 0)
}
export default BagItem
